import cn from 'classnames';

import { defineTheme, type ThemeKeys } from 'js/utils/theme-helpers';
import { DocumentIcon_IconThemeMap, getObjectByEnumMap } from 'js/utils/enum-string-maps';

import fallback from 'images/fallback.svg';
import excel from 'images/xls.svg';
import pdf from 'images/pdf.svg';
import ppt from 'images/ppt.svg';
import word from 'images/doc.svg';
import csv from 'images/csv.svg';
import image from 'images/image.svg';

import csvRequired from 'images/csv-required.svg';
import imageRequired from 'images/image-required.svg';
import fallbackRequired from 'images/fallback-required.svg';
import excelRequired from 'images/xls-required.svg';
import pdfRequired from 'images/pdf-required.svg';
import pptRequired from 'images/ppt-required.svg';
import wordRequired from 'images/doc-required.svg';

import csvBlack from 'images/csv-black.svg';
import imageBlack from 'images/image-black.svg';
import fallbackBlack from 'images/fallback-black.svg';
import excelBlack from 'images/xls-black.svg';
import pdfBlack from 'images/pdf-black.svg';
import pptBlack from 'images/ppt-black.svg';
import wordBlack from 'images/doc-black.svg';

import { DocumentIcon_IconTheme, IDocumentIcon } from 'source/NFR.types';

const sizes = defineTheme({
  xsmall: 'document-icon--icon-size-xsmall',
  small: 'document-icon--icon-size-small',
  large: 'document-icon--icon-size-large',
  xlarge: 'document-icon--icon-size-xlarge'
});

const iconThemes = {
  word: { icon: word, required: wordRequired, black: wordBlack },
  pdf: { icon: pdf, required: pdfRequired, black: pdfBlack },
  ppt: { icon: ppt, required: pptRequired, black: pptBlack },
  excel: { icon: excel, required: excelRequired, black: excelBlack },
  fallback: {
    icon: fallback,
    required: fallbackRequired,
    black: fallbackBlack
  },
  csv: { icon: csv, required: csvRequired, black: csvBlack },
  image: { icon: image, required: imageRequired, black: imageBlack }
};

type SizeValue = ThemeKeys<typeof sizes>;

interface DocumentIconProps extends IDocumentIcon {
  size?: SizeValue;
  className?: string;
}

const DocumentIcon: React.FC<DocumentIconProps> = ({
  iconTheme = DocumentIcon_IconTheme.Fallback,
  size,
  isRequired = false,
  isBlack = false,
  className
}) => {
  const getIcon = (): string => {
    const iconObj = getObjectByEnumMap(DocumentIcon_IconThemeMap, iconThemes, iconTheme) || iconThemes.fallback;
    if (isRequired && iconObj.required) return iconObj.required;
    if (isBlack && iconObj.black) return iconObj.black;
    return iconObj.icon;
  };

  const icon = getIcon();
  const backgroundImage = { backgroundImage: `url(${icon})` };

  return (
    <div className={cn('document-icon', className)}>
      <div className={cn('document-icon--icon', iconTheme, sizes(size))} style={backgroundImage} />
    </div>
  );
};

DocumentIcon.displayName = 'DocumentIcon';

export default DocumentIcon;
